// FormBaseMixin.ts - Common form functionality for onboarding forms

import {mapActions, mapGetters} from 'vuex';
import {MerchantOnboardingResponse} from "@/api/paymentApi";

// Define expected methods/fields that should be implemented by components using this mixin
interface FormBaseContext {
    shouldResubmit: boolean;

    validate(): Promise<void>;

    getErrors: Record<string, any>;
    isDirty: boolean;
    submitting: boolean;

    resetDirty(): void;

    setErrors(payload: { key: string; errors: string }): void;

    updateFieldFromEvent(payload: { key: string; event: Event }): void;

    updateFieldFromValue(payload: { key: string; value: any }): void;

    $danger(msg: string): void;

    $emit(event: string, ...args: any[]): void;
}

function ctx(vm: any): FormBaseContext {
    return vm as FormBaseContext;
}

export const OnboardingFormMixin = {
    props: {
        shouldResubmit: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            submitting: false,
        };
    },

    watch: {
        isDirty: {
            handler(newValue) {
                if (newValue) {
                    ctx(this).$emit('modified')
                }
            },
            immediate: true,
        },
    },

    computed: {
        ...mapGetters('app', ['getMerchantId', 'getSessionPayfac']),
    },

    methods: {
        ...mapActions('merchantPayfacDetails', ['setValuesFromIncrementalOnboardingResponse']),
        /**
         * Creates a field change handler that updates the store
         * @param {string} key - The field key to update
         */
        handleInputChange(key: string) {
            return (event: Event) => {
                ctx(this).updateFieldFromEvent({key, event});
            };
        },

        setInputValue(key: string, value: any) {
            ctx(this).updateFieldFromValue({key, value});
        },

        /**
         * Reset dirty state and navigate to next page
         */
        nextPage() {
            ctx(this).resetDirty();
            ctx(this).$emit('proceed');
        },

        /**
         * Common error handling for API calls
         * @param error - The error object
         */
        handleError(error: any) {
            if (error.source?.sub_errors?.length) {
                ctx(this).$danger?.('Validation failed!');
                error.source.sub_errors.forEach((subError: any) => {
                    if (!subError.field) {
                        ctx(this).$danger?.(subError.message);
                        return;
                    }
                    ctx(this).setErrors({
                        key: subError.field,
                        errors: subError.message,
                    });
                });
            } else {
                ctx(this).$danger?.(error.source?.title);
            }
        },

        /**
         * Common form submission logic with validation
         * @param apiCallFn - Function that makes the API call
         */
        async submitForm(apiCallFn: () => Promise<MerchantOnboardingResponse>, successCallBack?: () => void, skipValidation = false) {
            const self = ctx(this);

            try {
                if (!skipValidation) {
                    await self.validate();
                    if (Object.keys(self.getErrors || {}).length > 0) {
                        window.scrollTo(0, 0);
                        return;
                    }
                }

                self.submitting = true;
                const response = await apiCallFn();
                this.nextPage();
                await this.setValuesFromIncrementalOnboardingResponse(response);
                successCallBack && successCallBack();
            } catch (error) {
                this.handleError(error);
            } finally {
                self.submitting = false;
            }
        },
    },
};
